import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useReportManagement } from '../../../hooks/useReportManagement';
import { useNotification } from '../../../hooks';

const STATUS_OPTIONS = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'OVERDUE'];

const UpdateReportStatusDialog = ({ open, report, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { updateReportStatus, autoAddReportToFlows } = useReportManagement();
  const { showNotification } = useNotification();

  const [status, setStatus] = useState(report?.status || 'PENDING');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && report) {
      setStatus(report.status || 'PENDING');
      setError(null);
    }
  }, [open, report]);

  const firmName = report?.firmName || '';
  const reportTypeName = report?.reportTypeName || '';
  const subtitle = [firmName, reportTypeName].filter(Boolean).join(' — ');

  const handleSave = async () => {
    if (!report?.jobId || !report?.reportId) return;
    setLoading(true);
    setError(null);
    try {
      await updateReportStatus(report.jobId, report.reportId, status);

      // If the report is no longer COMPLETED it may now fall back into a
      // flow's scope. Best-effort re-sync — never blocks the status change.
      if (status !== 'COMPLETED') {
        try {
          await autoAddReportToFlows(report.jobId, report.reportId);
        } catch (autoAddErr) {
          console.error('Auto-add to flows after status change failed:', autoAddErr);
        }
      }

      showNotification(
        t('reportManagement.statusUpdate.success', { defaultValue: 'Statü güncellendi' }),
        'success'
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      const msg = err?.message
        || t('reportManagement.statusUpdate.error', { defaultValue: 'Statü güncellenemedi' });
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon color="primary" fontSize="small" />
        {t('reportManagement.statusUpdate.title', { defaultValue: 'Rapor Statüsü Güncelle' })}
      </DialogTitle>
      <DialogContent>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        <Box sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="report-status-label">
              {t('reportManagement.statusUpdate.statusLabel', { defaultValue: 'Yeni Statü' })}
            </InputLabel>
            <Select
              labelId="report-status-label"
              value={status}
              label={t('reportManagement.statusUpdate.statusLabel', { defaultValue: 'Yeni Statü' })}
              onChange={(e) => setStatus(e.target.value)}
              disabled={loading}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(`reportManagement.statuses.${s}`, { defaultValue: s })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {t('reportManagement.statusUpdate.cancel', { defaultValue: 'İptal' })}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || !status || status === report?.status}
          startIcon={loading ? <CircularProgress size={16} /> : <EditIcon fontSize="small" />}
        >
          {t('reportManagement.statusUpdate.save', { defaultValue: 'Kaydet' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateReportStatusDialog;
